// Exceeds target ceiling — single responsibility justified (prospect entity management)
import { supabase } from '../../lib/supabase'
import { getCurrentUserId, verifyPropertyWriteAccess, verifyAgencyWriteAccess } from '../../lib/auth'
import { resolveProspectDocumentUrls } from './referencing'
import type {
  Prospect,
  ProspectInsert,
  ProspectUpdate,
  ProspectPropertyInterest,
  ProspectPropertyInterestInsert,
  ProspectStatus
} from '../../types/prospect'

// ============================================================
// PROSPECTS
// ============================================================

export async function getProspects(status?: ProspectStatus, propertyIds?: string[]): Promise<Prospect[]> {
  // No client-side owner_id filter — RLS scopes to owner (owner_id = auth.uid())
  // or team writer (owner_id = get_agency_owner_id(auth.uid()))
  let query = supabase
    .from('prospects')
    .select(`
      *,
      user:profiles!prospects_user_id_fkey(id, full_name, email, phone),
      property_interests:prospect_property_interests(
        *,
        property:properties(id, name, address_line1, city)
      ),
      viewings:prospect_viewings(
        id,
        status,
        scheduled_at,
        declined_reason,
        outcome
      ),
      applications:prospect_applications(
        id,
        status,
        referencing:prospect_referencing(
          id,
          status,
          required_documents,
          documents:prospect_referencing_documents(id, document_type)
        )
      )
    `)
    .is('deleted_at', null)
    .order('created_at', { ascending: false })

  if (status) {
    query = query.eq('status', status)
  }

  const { data, error } = await query

  if (error) throw error

  // Portfolio filter (3-state): undefined = all, [] = none, [ids] = specific
  if (propertyIds === undefined) return data

  const pidSet = new Set(propertyIds)
  return data.filter((p: Prospect) => {
    const interests = (p as { property_interests?: { property_id?: string }[] }).property_interests
    if (!interests || interests.length === 0) return false
    return interests.some(pi => pi.property_id && pidSet.has(pi.property_id))
  })
}

export async function getProspect(id: string): Promise<Prospect> {
  // No client-side owner_id filter — RLS scopes access
  const { data, error } = await supabase
    .from('prospects')
    .select(`
      *,
      user:profiles!prospects_user_id_fkey(id, full_name, email, phone),
      viewings:prospect_viewings(
        *,
        property:properties(*),
        unit:units(*)
      ),
      applications:prospect_applications(
        *,
        property:properties(*),
        unit:units(*),
        referencing:prospect_referencing(
          *,
          documents:prospect_referencing_documents(*)
        )
      ),
      property_interests:prospect_property_interests(
        *,
        property:properties(*),
        unit:units(*)
      )
    `)
    .eq('id', id)
    .is('deleted_at', null)
    .single()

  if (error) throw error
  return await resolveProspectDocumentUrls(data)
}

export async function getProspectByUserId(userId: string, options?: { includeArchived?: boolean }): Promise<Prospect | null> {
  // No client-side owner_id filter — RLS scopes access for owner, team writer, and prospect-self
  let query = supabase
    .from('prospects')
    .select(`
      *,
      viewings:prospect_viewings(
        *,
        property:properties(*),
        unit:units(*)
      ),
      applications:prospect_applications(
        *,
        property:properties(*),
        referencing:prospect_referencing(
          *,
          documents:prospect_referencing_documents(*)
        )
      ),
      property_interests:prospect_property_interests(
        *,
        property:properties(*)
      )
    `)
    .eq('user_id', userId)
    .is('deleted_at', null)

  // Exclude converted prospects unless includeArchived is true
  if (!options?.includeArchived) {
    query = query.not('status', 'eq', 'converted')
  }

  const { data, error } = await query
    .order('created_at', { ascending: false })
    .limit(1)
    .single()

  if (error) {
    if (error.code === 'PGRST116') return null
    throw error
  }
  return await resolveProspectDocumentUrls(data)
}

/**
 * Prospect self-lookup: fetches the current user's own prospect record.
 * Uses RLS ("Prospects can view own record") instead of owner_id filter,
 * so this works when the prospect is logged in viewing their own portal.
 */
export async function getMyProspectRecord(options?: { includeArchived?: boolean }): Promise<Prospect | null> {
  const currentUserId = await getCurrentUserId()

  let query = supabase
    .from('prospects')
    .select(`
      *,
      viewings:prospect_viewings(
        *,
        property:properties(*),
        unit:units(*)
      ),
      applications:prospect_applications(
        *,
        property:properties(*),
        referencing:prospect_referencing(
          *,
          documents:prospect_referencing_documents(*)
        )
      ),
      property_interests:prospect_property_interests(
        *,
        property:properties(*)
      )
    `)
    .eq('user_id', currentUserId)
    .is('deleted_at', null)

  if (!options?.includeArchived) {
    query = query.not('status', 'eq', 'converted')
  }

  const { data, error } = await query
    .order('created_at', { ascending: false })
    .limit(1)
    .single()

  if (error) {
    if (error.code === 'PGRST116') return null
    throw error
  }
  return await resolveProspectDocumentUrls(data)
}

export async function createProspect(input: ProspectInsert, ownerId?: string): Promise<Prospect> {
  await verifyAgencyWriteAccess()

  const { data, error } = await supabase
    .from('prospects')
    .insert({
      ...input,
      owner_id: ownerId ?? (await getCurrentUserId()),
    })
    .select()
    .single()

  if (error) {
    if (error.code === '23505') {
      throw new Error('A prospect with this email already exists')
    }
    throw error
  }
  return data
}

export async function updateProspect(id: string, input: ProspectUpdate): Promise<Prospect> {
  // No client-side owner_id filter — RLS UPDATE policies scope access
  const { data, error } = await supabase
    .from('prospects')
    .update(input)
    .eq('id', id)
    .select()
    .single()

  if (error) throw error
  return data
}

export async function deleteProspect(id: string): Promise<void> {
  // SECURITY DEFINER RPC — bypasses the SELECT-visibility problem
  // (all prospect SELECT policies filter deleted_at IS NULL, so a plain
  // .update() can never pass RLS after setting deleted_at).
  // Auth resolved server-side from auth.uid() — no client owner value.
  const { error } = await supabase.rpc('soft_delete_prospect', { p_prospect_id: id })
  if (error) throw error
}

export async function updateProspectStatus(id: string, status: ProspectStatus): Promise<Prospect> {
  return updateProspect(id, { status })
}

/**
 * Reactivate a rejected/withdrawn prospect for a specific property.
 * Creates a new property interest and sets status back to 'lead'.
 */
export async function reactivateProspect(prospectId: string, propertyId: string): Promise<Prospect> {
  // Verify the current user can write to the property (owner, agent, or team writer)
  await verifyPropertyWriteAccess(propertyId)

  // Verify the prospect exists and is accessible (RLS enforces team/owner access)
  const { data: prospect } = await supabase
    .from('prospects')
    .select('id')
    .eq('id', prospectId)
    .is('deleted_at', null)
    .maybeSingle()
  if (!prospect) throw new Error('Prospect not found')

  // Check if property interest already exists
  const { data: existingInterest } = await supabase
    .from('prospect_property_interests')
    .select('id, interest_level')
    .eq('prospect_id', prospectId)
    .eq('property_id', propertyId)
    .single()

  if (existingInterest) {
    // Update existing interest back to 'enquired'
    await supabase
      .from('prospect_property_interests')
      .update({ interest_level: 'enquired' })
      .eq('id', existingInterest.id)
  } else {
    // Create new property interest
    await supabase
      .from('prospect_property_interests')
      .insert({
        prospect_id: prospectId,
        property_id: propertyId,
        interest_level: 'enquired'
      })
  }

  // Update prospect status back to 'lead'
  const { data, error } = await supabase
    .from('prospects')
    .update({ status: 'lead' })
    .eq('id', prospectId)
    .select()
    .single()

  if (error) throw error
  return data
}

// Recalculate prospect status based on existing stages
export async function recalculateProspectStatus(prospectId: string): Promise<void> {
  // Get current prospect status
  const { data: prospect } = await supabase
    .from('prospects')
    .select('status')
    .eq('id', prospectId)
    .is('deleted_at', null)
    .single()

  if (!prospect) return

  // Don't recalculate if already converted, rejected, or withdrawn
  if (['converted', 'rejected', 'withdrawn'].includes(prospect.status)) return

  // Check for active applications (skip rejected/withdrawn)
  const { data: applications } = await supabase
    .from('prospect_applications')
    .select('id, status, referencing:prospect_referencing(id, status)')
    .eq('prospect_id', prospectId)
    .is('deleted_at', null)

  const activeApplications = (applications || []).filter(
    a => !['rejected', 'withdrawn'].includes(a.status)
  )

  if (activeApplications.length > 0) {
    const app = activeApplications[0]

    if (app.status === 'approved') {
      await supabase.from('prospects').update({ status: 'approved' }).eq('id', prospectId)
      return
    }

    // Both application and referencing stages use 'application' status
    await supabase.from('prospects').update({ status: 'application' }).eq('id', prospectId)
    return
  }

  // Check for viewings
  const { data: viewings } = await supabase
    .from('prospect_viewings')
    .select('id')
    .eq('prospect_id', prospectId)
    .is('deleted_at', null)

  if (viewings && viewings.length > 0) {
    await supabase.from('prospects').update({ status: 'viewing' }).eq('id', prospectId)
    return
  }

  // Default to lead
  await supabase.from('prospects').update({ status: 'lead' }).eq('id', prospectId)
}

// ============================================================
// PROPERTY INTERESTS
// ============================================================

export async function addPropertyInterest(input: ProspectPropertyInterestInsert): Promise<ProspectPropertyInterest> {
  // Verify the current user can write to the property (owner, agent, or team writer)
  await verifyPropertyWriteAccess(input.property_id)

  const { data, error } = await supabase
    .from('prospect_property_interests')
    .insert(input)
    .select(`
      *,
      property:properties(*),
      unit:units(*)
    `)
    .single()

  if (error) throw error
  return data
}

export async function updatePropertyInterest(
  id: string,
  updates: Partial<ProspectPropertyInterestInsert>
): Promise<ProspectPropertyInterest> {
  // Verify ownership via the interest's property
  const { data: interest } = await supabase
    .from('prospect_property_interests')
    .select('property_id')
    .eq('id', id)
    .single()
  if (!interest) throw new Error('Property interest not found')
  await verifyPropertyWriteAccess(interest.property_id)

  const { data, error } = await supabase
    .from('prospect_property_interests')
    .update(updates)
    .eq('id', id)
    .select()
    .single()

  if (error) throw error
  return data
}

export async function removePropertyInterest(id: string): Promise<void> {
  // Verify ownership via the interest's property
  const { data: interest } = await supabase
    .from('prospect_property_interests')
    .select('property_id')
    .eq('id', id)
    .single()
  if (!interest) throw new Error('Property interest not found')
  await verifyPropertyWriteAccess(interest.property_id)

  const { error } = await supabase
    .from('prospect_property_interests')
    .delete()
    .eq('id', id)

  if (error) throw error
}

// ============================================================
// PROPERTY-BASED QUERIES
// ============================================================

export async function getProspectsByProperty(propertyId: string): Promise<Prospect[]> {
  // Fetch all prospect IDs linked to this property (via interests, viewings, or applications)
  // using a single server-side UNION RPC instead of three separate junction table queries.
  const { data: idsData, error: idsError } = await supabase.rpc('get_prospect_ids_for_property', {
    p_property_id: propertyId,
  })
  if (idsError) throw idsError

  const prospectIds = new Set<string>((idsData || []).map((r: { prospect_id: string }) => r.prospect_id))

  if (prospectIds.size === 0) return []

  // Fetch full prospect data with viewings for this property
  const { data, error } = await supabase
    .from('prospects')
    .select(`
      *,
      user:profiles!prospects_user_id_fkey(id, full_name, email, phone),
      viewings:prospect_viewings(
        *,
        property:properties(id, address_line1, city)
      ),
      applications:prospect_applications(
        *,
        property:properties(id, address_line1, city)
      ),
      property_interests:prospect_property_interests(
        *,
        property:properties(id, address_line1, city)
      )
    `)
    .in('id', Array.from(prospectIds))
    .is('deleted_at', null)
    .order('created_at', { ascending: false })

  if (error) throw error

  // Filter viewings/applications to only show ones for this property
  return (data || []).map(p => ({
    ...p,
    viewings: (p.viewings || []).filter((v: { property_id: string }) => v.property_id === propertyId),
    applications: (p.applications || []).filter((a: { property_id: string }) => a.property_id === propertyId),
    property_interests: (p.property_interests || []).filter((i: { property_id: string }) => i.property_id === propertyId),
  })) as Prospect[]
}

// ============================================================
// PENDING TENANTS (prospects linked to tenancy without accounts)
// ============================================================

/**
 * Get prospects that are linked to a tenancy but don't have user accounts yet.
 * These are "pending tenants" who need to complete registration.
 * Checks both notes (legacy) and converted_tenancy_id (existing tenants).
 */
export async function getPendingTenantsForTenancy(tenancyId: string): Promise<Prospect[]> {
  // Get prospects linked to this tenancy without a user account
  // Run two queries since .or() with .is() doesn't combine well
  const [notesResult, convertedResult] = await Promise.all([
    // Legacy: notes contain tenancy ID
    supabase
      .from('prospects')
      .select(`
        *,
        property_interests:prospect_property_interests(*)
      `)
      .is('user_id', null)
      .is('deleted_at', null)
      .ilike('notes', `%${tenancyId}%`),
    // New: converted_tenancy_id matches
    supabase
      .from('prospects')
      .select(`
        *,
        property_interests:prospect_property_interests(*)
      `)
      .is('user_id', null)
      .is('deleted_at', null)
      .eq('converted_tenancy_id', tenancyId)
  ])

  if (notesResult.error) throw notesResult.error
  if (convertedResult.error) throw convertedResult.error

  // Combine and deduplicate by id
  const combined = [...(notesResult.data || []), ...(convertedResult.data || [])]
  const uniqueMap = new Map(combined.map(p => [p.id, p]))
  return Array.from(uniqueMap.values())
}

// ============================================================
// PROSPECT ACTION HELPERS
// ============================================================

